<script setup lang="ts">
import { computed } from 'vue'
import type { AnyCalculatorDefinition } from '@/types/calculator'
import type { ResultRow } from '@/components/calculator/ResultTable.vue'
import { useFormatters } from '@/composables/useFormatters'
import { useAppStore } from '@/stores/app'
import Card from '@/components/ui/Card.vue'
import ResultTable from '@/components/calculator/ResultTable.vue'
import FaqList from '@/components/calculator/FaqList.vue'

/**
 * Long-form SEO content rendered below the calculator: intro, how it works,
 * formulas, a live-computed worked example, key terms and FAQs.
 */
const props = defineProps<{
  definition: AnyCalculatorDefinition
}>()

const app = useAppStore()
const { formatCurrency, formatPercent, formatNumber } = useFormatters()

/**
 * Display name used in section headings, e.g. "Bond Calculator" or
 * "VAT calculator" — keeps keyworded H2s per calculator without
 * duplicating "calculator" when the short title already ends with it.
 */
const calcName = computed(() => {
  const base = props.definition.shortTitle || props.definition.title
  return /calculator$/i.test(base) ? base : `${base} calculator`
})

/** Label/value rows describing the worked example's inputs. */
const exampleInputRows = computed<ResultRow[]>(() =>
  props.definition.inputs
    .filter((field) => props.definition.content.example.inputs[field.name] !== undefined)
    .map((field) => {
      const raw = props.definition.content.example.inputs[field.name] as number | string | boolean
      let value: string | number = raw as string | number
      if (typeof raw === 'boolean') {
        value = raw ? 'Yes' : 'No'
      } else if (field.type === 'money' && typeof raw === 'number') {
        value = formatCurrency(raw, app.currency)
      } else if (field.type === 'percentage' && typeof raw === 'number') {
        value = formatPercent(raw)
      } else if (field.type === 'radio' || field.type === 'select') {
        value = field.options?.find((o) => o.value === raw)?.label ?? String(raw)
      } else if (typeof raw === 'number' && field.unit) {
        value = `${raw} ${field.unit}`
      }
      return { label: field.label, value, format: 'plain' }
    })
)

/** The example result, computed live so figures can never go stale. */
const exampleResult = computed(() =>
  props.definition.calculate(props.definition.content.example.inputs, {
    taxYear: app.taxYear,
    currency: app.currency,
  })
)

const exampleResultRows = computed<ResultRow[]>(() =>
  props.definition.resultSections.map((section) => ({
    label: section.label,
    value: section.value(exampleResult.value),
    format: section.format,
  }))
)

function formatValue(value: string | number, format?: ResultRow['format']): string {
  if (typeof value === 'string') return value
  switch (format) {
    case 'currency':
      return formatCurrency(value, app.currency)
    case 'percent':
      return formatPercent(value)
    case 'number':
      return formatNumber(value, 2)
    default:
      return String(value)
  }
}
</script>

<template>
  <Card class="content" padding="lg">
    <article class="content__prose">
      <h2>About the {{ calcName }}</h2>
      <p class="content__intro">{{ definition.content.intro }}</p>

      <h2>How the {{ calcName }} works</h2>
      <p v-for="(paragraph, index) in definition.content.howItWorks" :key="index">
        {{ paragraph }}
      </p>

      <h2>The {{ calcName }} formula</h2>
      <dl class="content__formulas">
        <div v-for="line in definition.content.formulas" :key="line.label" class="content__formula">
          <dt>{{ line.label }}</dt>
          <dd>
            <code>{{ line.expression }}</code>
            <span v-if="line.notes" class="content__formula-notes">{{ line.notes }}</span>
          </dd>
        </div>
      </dl>

      <h2>Worked example</h2>
      <p>{{ definition.content.example.description }}</p>
      <h3>{{ definition.content.example.title }}</h3>
      <ResultTable :rows="exampleInputRows" caption="Example inputs" />
      <h3>Results</h3>
      <dl class="content__results">
        <div v-for="row in exampleResultRows" :key="row.label" class="content__result-row">
          <dt>{{ row.label }}</dt>
          <dd>{{ formatValue(row.value, row.format) }}</dd>
        </div>
      </dl>

      <template v-if="definition.content.definitions?.length">
        <h2>Key terms</h2>
        <dl class="content__definitions">
          <div
            v-for="definitionItem in definition.content.definitions"
            :key="definitionItem.term"
            class="content__definition"
          >
            <dt>{{ definitionItem.term }}</dt>
            <dd>{{ definitionItem.description }}</dd>
          </div>
        </dl>
      </template>

      <FaqList :faqs="definition.faqs" />
    </article>
  </Card>
</template>

<style scoped>
.content {
  margin-top: var(--space-8);
}

.content__prose {
  max-width: 75ch;
  color: var(--color-text);
  line-height: var(--leading-relaxed);
}

.content__prose h2 {
  margin-top: var(--space-8);
  font-size: var(--text-xl);
  font-weight: var(--font-semibold);
}

.content__prose h2:first-child {
  margin-top: 0;
}

.content__prose h3 {
  margin-top: var(--space-5);
  font-size: var(--text-base);
  font-weight: var(--font-semibold);
}

.content__prose p {
  margin-top: var(--space-3);
}

.content__intro {
  font-size: var(--text-lg);
}

.content__formulas,
.content__definitions,
.content__results {
  margin: var(--space-4) 0 0;
}

.content__formula,
.content__definition {
  margin-top: var(--space-3);
}

.content__formula dt,
.content__definition dt,
.content__result-row dt {
  font-weight: var(--font-semibold);
}

.content__formula dd,
.content__definition dd,
.content__result-row dd {
  margin: var(--space-1) 0 0;
}

.content__formula code {
  font-family: var(--font-mono);
  font-size: var(--text-sm);
  background: var(--color-surface-raised);
  padding: var(--space-1) var(--space-2);
  border-radius: var(--radius-sm);
}

.content__formula-notes {
  display: block;
  margin-top: var(--space-1);
  color: var(--color-text-muted);
  font-size: var(--text-sm);
}

.content__result-row {
  display: flex;
  justify-content: space-between;
  gap: var(--space-4);
  padding: var(--space-2) 0;
  border-bottom: 1px solid var(--color-border);
}
</style>
